import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router';

import { ContactForm } from '../../components/contacts/contact-form.tsx';
import { Alert } from '../../components/ui/alert.tsx';
import { Loading } from '../../components/ui/skeleton.tsx';
import { errorMessage } from '../../lib/api.ts';
import { contactKeys, useContact, type ContactDetail } from '../../lib/contacts-api.ts';
import { useToast } from '../../lib/toast.tsx';
import { NotFoundPage } from '../status-pages.tsx';

function useSaved(message: string) {
  const client = useQueryClient();
  const navigate = useNavigate();
  const notify = useToast();
  return async (saved: ContactDetail) => {
    client.setQueryData(contactKeys.detail(saved.id), saved);
    await client.invalidateQueries({ queryKey: contactKeys.all });
    notify(message, 'success');
    await navigate(`/contactos/${saved.id}`);
  };
}

export function NewContactPage() {
  const saved = useSaved('Contacto creado.');
  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <div>
        <Link to="/contactos" className="text-sm text-primary hover:underline">
          ← Contactos
        </Link>
        <h1 className="text-2xl font-semibold">Nuevo contacto</h1>
      </div>
      <ContactForm submitLabel="Crear contacto" onSaved={(c) => void saved(c)} />
    </div>
  );
}

export function EditContactPage() {
  const { id = '' } = useParams();
  const contact = useContact(id);
  const saved = useSaved('Cambios guardados.');
  if (contact.isPending) return <Loading />;
  if (contact.error) {
    return (contact.error as { status?: number }).status === 404 ? (
      <NotFoundPage />
    ) : (
      <Alert tone="danger">{errorMessage(contact.error)}</Alert>
    );
  }
  if (!contact.data.can.edit) return <NotFoundPage />;
  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <div>
        <Link to={`/contactos/${id}`} className="text-sm text-primary hover:underline">
          ← {contact.data.name}
        </Link>
        <h1 className="text-2xl font-semibold">Editar contacto</h1>
      </div>
      <ContactForm
        contact={contact.data}
        submitLabel="Guardar cambios"
        onSaved={(c) => void saved(c)}
      />
    </div>
  );
}
